import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { publicApi } from "../../admin/api";
import { needsInvert, resolveImage } from "../../lib/data";
import { useSettings } from "../../lib/useSettings";
import { usePageContent } from "../../lib/usePageContent";
import RevealClip from "../RevealClip";

const DEFAULTS = {
  openings_eyebrow: "Progetti futuri",
  openings_title: "Le prossime",
  openings_title_accent: "aperture.",
  openings_description:
    "Nuovi progetti stanno prendendo forma in aree strategiche di Milano. Cinque strutture, oltre 674 camere, snodi chiave della città: da Piazza Duomo a Rho Fiera.",
};

export default function FutureOpenings() {
  const [items, setItems] = useState([]);
  const c = usePageContent("home", DEFAULTS);
  const settings = useSettings();
  const showAllCta = !!settings?.home_openings_show_all_cta;

  useEffect(() => {
    publicApi.get("/openings").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  if (!items.length) return null;

  return (
    <section
      data-testid="future-openings-section"
      className="relative py-24 md:py-36 px-6 md:px-12 max-w-[1440px] mx-auto"
    >
      <div className="grid lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
            <span className="h-px w-8 bg-[#C48F56]" />
            {c.openings_eyebrow || DEFAULTS.openings_eyebrow}
          </div>
          <h2 className="mt-5 font-display text-5xl md:text-6xl lg:text-7xl tracking-tightest">
            {c.openings_title || DEFAULTS.openings_title}{" "}
            <span className="text-[#C48F56]">
              {c.openings_title_accent || DEFAULTS.openings_title_accent}
            </span>
          </h2>
        </div>
        <div className="lg:col-span-6 lg:pt-10">
          <p className="text-white/60 text-lg leading-relaxed">
            {c.openings_description || DEFAULTS.openings_description}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {items.map((p, i) => (
          <motion.div
            key={p.id}
            id={`opening-${p.slug}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{
              duration: 0.7,
              delay: i * 0.05,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="group relative grid md:grid-cols-12 gap-6 items-center rounded-2xl border border-white/10 bg-[#0F0F14] p-4 md:p-6 hover:border-[#C48F56]/40 transition-colors overflow-hidden scroll-mt-28 target:border-[#C48F56] target:shadow-[0_0_0_2px_rgba(196,143,86,0.35)]"
          >
            <div className="md:col-span-3 relative aspect-[4/3] md:aspect-square rounded-xl overflow-hidden bg-[#08080A]">
              {p.image ? (
                <RevealClip direction="up" duration={1.1} delay={i * 0.05} className="absolute inset-0">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.2s] group-hover:scale-110"
                    style={{ backgroundImage: `url(${resolveImage(p.image)})` }}
                  />
                  {/* Bottom gradient for legibility of badges */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-black/30" />
                </RevealClip>
              ) : (
                <div className="absolute inset-0">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(196,143,86,0.15)_0%,transparent_70%)]" />
                  <div
                    className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
                    style={{
                      backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                    }}
                  />
                  {p.logo && (
                    <img
                      src={p.logo}
                      alt={p.name}
                      className={`absolute inset-0 m-auto h-2/3 w-2/3 object-contain opacity-85 ${
                        needsInvert(p.logo) ? "brightness-0 invert" : ""
                      }`}
                    />
                  )}
                </div>
              )}
              <div className="absolute top-3 left-3 glass rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em]">
                {"★".repeat(p.stars)}
              </div>
              <div className="absolute bottom-3 right-3 glass rounded-full px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] text-[#C48F56]">
                In progetto
              </div>
            </div>
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-[#C48F56]">
                <Calendar size={12} />
                {p.opening}
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span className="text-white/50">{p.area}</span>
              </div>
              <h3 className="mt-3 font-display text-3xl md:text-4xl tracking-tight">
                {p.name}
              </h3>
              <p className="mt-3 text-white/60 text-sm md:text-base leading-relaxed max-w-2xl">
                {p.description}
              </p>
            </div>
            <div className="md:col-span-2 md:text-right">
              {p.project_url ? (
                <a
                  href={p.project_url}
                  target={p.project_url.startsWith("http") ? "_blank" : undefined}
                  rel={p.project_url.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid={`opening-cta-${p.slug}`}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-[#C48F56] hover:text-black hover:border-[#C48F56] transition-colors"
                >
                  Il progetto <ArrowUpRight size={14} />
                </a>
              ) : null}
            </div>
          </motion.div>
        ))}
      </div>

      {showAllCta && (
        <div className="mt-12 text-center">
          <Link
            to="/nuove-aperture"
            data-testid="openings-view-all"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm hover:bg-white hover:text-black transition-colors"
          >
            Tutte le nuove aperture <ArrowUpRight size={14} />
          </Link>
        </div>
      )}
    </section>
  );
}
